// Modelo de UTM dos anúncios da Meta (#318): helpers puros, sem React e sem rede.
// A regra de verdade (macros aceitas, caracteres, tamanho) é do backend (`api/meta_ads/utm.py`);
// aqui validamos igual só para o usuário ver o erro antes de salvar, e montamos o link de exemplo.

export const UTM_FIELDS = [
  { key: 'source', param: 'utm_source', label: 'Origem', help: 'De onde vem o clique. Ex.: facebook' },
  { key: 'medium', param: 'utm_medium', label: 'Mídia', help: 'Tipo de tráfego. Ex.: paid_social' },
  { key: 'campaign', param: 'utm_campaign', label: 'Campanha', help: 'Ex.: {{campaign.name}}' },
  { key: 'content', param: 'utm_content', label: 'Conteúdo', help: 'Ex.: {{ad.name}}' },
  { key: 'term', param: 'utm_term', label: 'Termo', help: 'Opcional. Ex.: {{adset.name}}' },
]

// Mesmas macros do backend (`utm.MACROS`); a tela usa a lista que o servidor devolve e cai nesta se faltar.
export const DEFAULT_MACROS = [
  { token: '{{campaign.name}}', label: 'Nome da campanha' },
  { token: '{{campaign.id}}', label: 'ID da campanha' },
  { token: '{{adset.name}}', label: 'Nome do conjunto de anúncios' },
  { token: '{{adset.id}}', label: 'ID do conjunto de anúncios' },
  { token: '{{ad.name}}', label: 'Nome do anúncio' },
  { token: '{{ad.id}}', label: 'ID do anúncio' },
  { token: '{{placement}}', label: 'Posicionamento (Feed, Stories, Reels...)' },
]

// Valores fictícios só para o link de exemplo (a Meta troca pelos reais no clique).
export const SAMPLE_VALUES = {
  '{{campaign.name}}': 'Black Friday',
  '{{campaign.id}}': '120210000000001',
  '{{adset.name}}': 'Brasil 25-45',
  '{{adset.id}}': '120210000000002',
  '{{ad.name}}': 'Imagem 1',
  '{{ad.id}}': '120210000000003',
  '{{placement}}': 'Instagram_Feed',
}

export const MAX_LENGTH = 100
const MACRO = /\{\{[^{}]*\}\}/g
const SAFE_EXTRA = ' _-.~+|:/'

export const emptyTemplate = () => ({ source: '', medium: '', campaign: '', content: '', term: '' })

// Resposta do servidor (ou null) -> estado do formulário.
export const templateToForm = (template) => ({ ...emptyTemplate(), ...Object.fromEntries(UTM_FIELDS.map(({ key }) => [key, template?.[key] || ''])) })

export const isEmptyTemplate = (utm) => UTM_FIELDS.every(({ key }) => !(utm?.[key] || '').trim())

// Mesma regra do backend. Devolve a mensagem ou null.
export function validateUtmValue(value, macros = DEFAULT_MACROS) {
  const text = (value || '').trim()
  if (text.length > MAX_LENGTH) return `Use no máximo ${MAX_LENGTH} caracteres.`
  const known = new Set(macros.map((m) => m.token))
  for (const macro of text.match(MACRO) || []) {
    if (!known.has(macro)) return `Macro desconhecida: ${macro}. Use as macros da lista.`
  }
  const literal = text.replace(MACRO, '')
  const bad = [...literal].find((c) => !(/[\p{L}\p{N}]/u.test(c) || SAFE_EXTRA.includes(c)))
  return bad === undefined ? null : `O caractere "${bad}" não pode ser usado.`
}

export function validateTemplate(form, macros) {
  const errors = {}
  for (const { key } of UTM_FIELDS) {
    const message = validateUtmValue(form[key], macros)
    if (message) errors[key] = message
  }
  return errors
}

export const resolveMacros = (value, samples = SAMPLE_VALUES) =>
  (value || '').replace(MACRO, (macro) => samples[macro] ?? macro)

// Link final de exemplo: macros trocadas por valores fictícios, texto fixo codificado como URL.
export function exampleLink(link, utm) {
  const base = (link || '').trim() || 'https://seusite.com.br/pagina'
  const pairs = UTM_FIELDS
    .map(({ key, param }) => [param, resolveMacros((utm?.[key] || '').trim())])
    .filter(([, value]) => value)
    .map(([param, value]) => `${param}=${encodeURIComponent(value)}`)
  if (!pairs.length) return base
  return `${base}${base.includes('?') ? '&' : '?'}${pairs.join('&')}`
}

// Insere o texto na posição do cursor (ou no fim). Devolve { text, caret }.
export function insertAt(text, token, start, end) {
  const from = Number.isInteger(start) ? start : text.length
  const to = Number.isInteger(end) ? end : from
  return { text: `${text.slice(0, from)}${token}${text.slice(to)}`, caret: from + token.length }
}
